export const BUFFER_SIZE = 256;
export const DEFAULT_STOP_DEBOUNCE_MS = 500;
export const MIC_MODAL_COOLDOWN_MS = 10_000;
// Press shorter than this is treated as accidental and skips the silence check.
export const SILENCE_PRESS_DURATION_MIN_MS = 500;
export const PREFERRED_MIC_DEVICE_ID_KEY = "preferred_mic_device_id";

const MIC_ERROR_NAMES = new Set([
  "NotAllowedError",
  "PermissionDeniedError",
  "NotFoundError",
  "DevicesNotFoundError",
  "NotReadableError",
  "OverconstrainedError",
]);

const MIC_PERMISSION_DENIED_ERROR_NAMES = new Set([
  "NotAllowedError",
  "PermissionDeniedError",
  "SecurityError",
]);

type AudioContextLike = {
  state?: string;
} | null;

type MediaTrackLike = {
  readyState?: string;
  getSettings?: () => Record<string, unknown>;
};

type MediaStreamLike = {
  getTracks?: () => MediaTrackLike[];
} | null;

export function getMicAudioConstraints(
  deviceId?: string | null,
): MediaStreamConstraints {
  const audio: MediaTrackConstraints = {
    channelCount: 1,
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  };
  if (deviceId) {
    audio.deviceId = { exact: deviceId };
  }
  return { audio };
}

export function getMicCaptureProfile(
  stream: MediaStreamLike,
  constraints: MediaStreamConstraints = getMicAudioConstraints(),
) {
  const track = stream
    ?.getTracks?.()
    ?.find((item) => item.readyState !== "ended");
  const settings = track?.getSettings?.() ?? {};
  const requestedAudio =
    constraints.audio && typeof constraints.audio === "object"
      ? constraints.audio
      : {};
  return {
    requested: {
      channel_count: requestedAudio.channelCount,
      echo_cancellation: requestedAudio.echoCancellation,
      noise_suppression: requestedAudio.noiseSuppression,
      auto_gain_control: requestedAudio.autoGainControl,
    },
    applied: {
      channel_count: settings.channelCount,
      sample_rate: settings.sampleRate,
      sample_size: settings.sampleSize,
      echo_cancellation: settings.echoCancellation,
      noise_suppression: settings.noiseSuppression,
      auto_gain_control: settings.autoGainControl,
      latency: settings.latency,
    },
    client: {
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      platform:
        typeof navigator !== "undefined" ? navigator.platform : undefined,
    },
  };
}

export function loadPreferredMicDeviceId(): string | null {
  try {
    return localStorage.getItem(PREFERRED_MIC_DEVICE_ID_KEY);
  } catch {
    return null;
  }
}

export function savePreferredMicDeviceId(deviceId: string | null): void {
  try {
    if (deviceId) {
      localStorage.setItem(PREFERRED_MIC_DEVICE_ID_KEY, deviceId);
    } else {
      localStorage.removeItem(PREFERRED_MIC_DEVICE_ID_KEY);
    }
  } catch {
    // localStorage may be unavailable (private mode quotas); ignore.
  }
}

export function isPcmBufferAllZero(buffer: ArrayBuffer | undefined): boolean {
  if (!buffer || buffer.byteLength === 0) return true;
  const samples = new Int16Array(buffer);
  for (let i = 0; i < samples.length; i++) {
    if (samples[i] !== 0) return false;
  }
  return true;
}

export function getErrorName(error: unknown): string | undefined {
  return error instanceof Error
    ? error.name
    : (error as { name?: string } | null)?.name;
}

export function isPermissionDeniedState(state: string | undefined): boolean {
  return state === "denied";
}

export function isPermissionDeniedErrorName(
  errorName: string | undefined,
): boolean {
  return MIC_PERMISSION_DENIED_ERROR_NAMES.has(errorName ?? "");
}

export function isKnownMicErrorName(errorName: string | undefined): boolean {
  return MIC_ERROR_NAMES.has(errorName ?? "");
}

export function shouldShowMicModalForLastError({
  lastErrorTimeMs,
  nowMs,
  cooldownMs = MIC_MODAL_COOLDOWN_MS,
}: {
  lastErrorTimeMs: number;
  nowMs: number;
  cooldownMs?: number;
}): boolean {
  return nowMs - lastErrorTimeMs >= cooldownMs;
}

export function hasLiveAudioTrack(stream: MediaStreamLike): boolean {
  return (
    stream?.getTracks?.().some((track) => track.readyState === "live") ?? false
  );
}

export function isAudioGraphStale({
  isInitialized,
  mediaStream,
  audioContext,
  workletNode,
  sourceNode,
}: {
  isInitialized: boolean;
  mediaStream: MediaStreamLike;
  audioContext: AudioContextLike;
  workletNode: unknown;
  sourceNode: unknown;
}): boolean {
  const hasAudioContext = audioContext !== null;
  const hasWorklet = workletNode !== null && sourceNode !== null;
  const contextState = audioContext?.state;
  const contextNotRunning =
    contextState !== undefined && contextState !== "running";

  return (
    !isInitialized ||
    !hasAudioContext ||
    contextNotRunning ||
    !hasLiveAudioTrack(mediaStream) ||
    !hasWorklet
  );
}
